import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getDashboardSummary, getBanks, getPayments, getExpectedReceipts, getAuditLogs } from '../api';
import { toast } from 'sonner';
import { BarChart3, Activity, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';

const AnalyticsPage = ({ user, onLogout }) => {
  const [summary, setSummary] = useState(null);
  const [banks, setBanks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, banksRes, paymentsRes, receiptsRes, logsRes] = await Promise.all([
        getDashboardSummary(),
        getBanks(),
        getPayments(),
        getExpectedReceipts(),
        getAuditLogs()
      ]);
      setSummary(summaryRes.data);
      setBanks(banksRes.data);
      setPayments(paymentsRes.data);
      setReceipts(receiptsRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const totalInflow = receipts
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + c.amount, 0);

  const totalOutflow = payments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);

  const netPosition = (summary?.total_balance || 0) + totalInflow - totalOutflow;

  const banksByBalance = [...banks].sort((a, b) => b.balance - a.balance);
  const topPayments = [...payments]
    .filter(p => p.status === 'pending')
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="analytics-page-title">
            Analytics & Reports
          </h1>
          <p className="text-slate-600">Insights into your fund flow and financial position</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading analytics...</div>
        ) : (
          <>
            {/* Financial Summary Cards */}
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Total Inflow (Pending)</p>
                  <p className="text-3xl font-bold text-blue-700" data-testid="total-inflow">
                    {formatCurrency(totalInflow)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">From {receipts.filter(c => c.status === 'pending').length} pending receipts</p>
                </CardContent>
              </Card>

              <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <TrendingDown className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Total Outflow (Pending)</p>
                  <p className="text-3xl font-bold text-red-700" data-testid="total-outflow">
                    {formatCurrency(totalOutflow)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">From {payments.filter(p => p.status === 'pending').length} pending payments</p>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Activity className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Net Position</p>
                  <p className="text-3xl font-bold text-green-700" data-testid="net-position">
                    {formatCurrency(netPosition)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">After all pending transactions</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Banks by Balance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Bank Accounts by Balance
                  </CardTitle>
                  <CardDescription>Top accounts ranked by available balance</CardDescription>
                </CardHeader>
                <CardContent>
                  {banksByBalance.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No bank accounts</p>
                  ) : (
                    <div className="space-y-4">
                      {banksByBalance.map((bank, idx) => (
                        <div key={bank.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{bank.account_name}</p>
                              <p className="text-sm text-slate-500">{bank.bank_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-900">{formatCurrency(bank.balance, bank.currency)}</p>
                            <p className="text-xs text-slate-500">{bank.currency}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Pending Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    Top Pending Payments
                  </CardTitle>
                  <CardDescription>Largest upcoming payment obligations</CardDescription>
                </CardHeader>
                <CardContent>
                  {topPayments.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No pending payments</p>
                  ) : (
                    <div className="space-y-4">
                      {topPayments.map((payment, idx) => (
                        <div key={payment.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-semibold text-sm">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{payment.vendor_name}</p>
                              <p className="text-sm text-slate-500">
                                Due: {format(new Date(payment.due_date), 'dd MMM yyyy')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-900">{formatCurrency(payment.amount, payment.currency)}</p>
                            <Badge variant="outline" className="mt-1">{payment.payment_type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Cash Flow Insights */}
            <Card>
              <CardHeader>
                <CardTitle>Cash Flow Insights</CardTitle>
                <CardDescription>Actionable recommendations based on your fund flow</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {netPosition > 0 && (
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-900 mb-1">Positive Cash Position</p>
                          <p className="text-sm text-green-700">
                            Your net position is {formatCurrency(netPosition)}. Consider deploying excess funds in short-term investments 
                            for payments due beyond 7 days.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {summary?.upcoming_payments > summary?.usable_balance && (
                    <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-orange-100">
                          <TrendingDown className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-orange-900 mb-1">Monitor Cash Position</p>
                          <p className="text-sm text-orange-700">
                            Upcoming payments ({formatCurrency(summary.upcoming_payments)}) exceed usable balance 
                            ({formatCurrency(summary.usable_balance)}). Ensure timely cheque clearances.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {receipts.filter(c => c.status === 'pending').length > 0 && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Activity className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-blue-900 mb-1">Pending Receipt Clearances</p>
                          <p className="text-sm text-blue-700">
                            You have {receipts.filter(c => c.status === 'pending').length} receipts awaiting clearance worth {formatCurrency(totalInflow)}. 
                            Track clearance dates to optimize cash deployment.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity (Audit Logs) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Audit trail of recent changes</CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No recent activity</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Action</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead>Timestamp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLogs.slice(0, 10).map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <Badge variant="outline">{log.action}</Badge>
                            </TableCell>
                            <TableCell className="capitalize">{log.entity_type}</TableCell>
                            <TableCell className="text-sm text-slate-600">{log.details || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {format(new Date(log.timestamp), 'dd MMM yyyy, HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
